import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, ChevronRight, Target, ClipboardList, Layers, Tag } from "lucide-react";
import { DIVISIONS, findDivision } from "../data/divisions";
import DivisionGallery from "../components/DivisionGallery";

/**
 * Divisione — pagina dedicata a una singola divisione (deep-link `/divisioni/:slug`).
 * Layout: hero istituzionale con codice divisione + descrizione lunga + card missione /
 * responsabilità / struttura + tag operativi + rimando alle altre divisioni.
 */
export default function Divisione() {
  const { slug } = useParams();
  const division = findDivision(slug);

  if (!division) {
    return <Navigate to="/divisioni" replace />;
  }

  const Icon = division.icon;
  const others = DIVISIONS.filter((d) => d.slug !== division.slug).slice(0, 4);

  return (
    <div data-testid={`division-page-${division.code}`} className="bg-[var(--lssd-olive-deep)]">

      {/* ============ HERO ============ */}
      <section
        className="relative border-b border-[var(--lssd-khaki)]/25 overflow-hidden"
        style={{
          backgroundImage: division.image ? `linear-gradient(180deg, rgba(15,26,10,0.72) 0%, rgba(15,26,10,0.92) 70%, var(--lssd-olive-deep) 100%), url(${division.image})` : undefined,
          backgroundSize: "cover",
          backgroundPosition: "center 30%",
        }}
      >
        <div className="max-w-6xl mx-auto px-6 sm:px-10 py-16 sm:py-24 relative">

          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] font-mono mb-8" data-testid="division-breadcrumb">
            <Link to="/" className="text-[var(--lssd-khaki)]/70 hover:text-[var(--lssd-khaki)] transition-colors">Home</Link>
            <ChevronRight className="h-3 w-3 text-[var(--lssd-khaki)]/40" />
            <Link to="/divisioni" className="text-[var(--lssd-khaki)]/70 hover:text-[var(--lssd-khaki)] transition-colors">Divisioni</Link>
            <ChevronRight className="h-3 w-3 text-[var(--lssd-khaki)]/40" />
            <span className="text-[var(--lssd-khaki)] font-semibold">{division.code}</span>
          </div>

          <div className="flex flex-col md:flex-row items-start gap-8">

            {/* Icona grande */}
            <div className="shrink-0 h-20 w-20 sm:h-24 sm:w-24 rounded-2xl bg-[var(--lssd-khaki)]/15 border-2 border-[var(--lssd-khaki)]/45 flex items-center justify-center">
              <Icon className="h-10 w-10 sm:h-12 sm:w-12 text-[var(--lssd-khaki)]" strokeWidth={1.8} />
            </div>

            {/* Titolo + descrizione lunga */}
            <div className="flex-1 min-w-0">
              <div className="font-mono text-[11px] uppercase tracking-[0.28em] font-bold text-[var(--lssd-khaki)] mb-3">
                Unit · {division.code}
              </div>
              <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl text-[var(--lssd-cream)] tracking-tight leading-[1.02] mb-5">
                {division.name}
              </h1>
              <p className="text-[var(--lssd-cream)]/85 text-lg sm:text-xl leading-relaxed max-w-3xl">
                {division.full}
              </p>

              {/* Tags */}
              <div className="mt-6 flex flex-wrap gap-2">
                {division.tags.map((t) => (
                  <span
                    key={t}
                    className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] font-bold px-3 py-1.5 rounded-full bg-[var(--lssd-olive-deep)]/70 border border-[var(--lssd-khaki)]/35 text-[var(--lssd-khaki)]"
                  >
                    <Tag className="h-2.5 w-2.5" /> {t}
                  </span>
                ))}
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ============ MISSIONE · RESPONSABILITÀ · STRUTTURA ============ */}
      <section className="py-16 sm:py-20 bg-[var(--lssd-olive-dark)] border-b border-[var(--lssd-khaki)]/15">
        <div className="max-w-6xl mx-auto px-6 sm:px-10">

          <div className="grid lg:grid-cols-3 gap-5 sm:gap-6">

            {/* MISSIONE */}
            <div className="bg-[var(--lssd-olive)] border border-[var(--lssd-khaki)]/25 rounded-2xl p-7" data-testid="division-mission">
              <div className="h-10 w-10 rounded-full bg-[var(--lssd-khaki)]/15 border border-[var(--lssd-khaki)]/40 flex items-center justify-center mb-4">
                <Target className="h-4 w-4 text-[var(--lssd-khaki)]" />
              </div>
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] font-bold text-[var(--lssd-khaki)] mb-2">
                Missione
              </div>
              <p className="text-[var(--lssd-cream)]/85 text-[15px] leading-relaxed">
                {division.mission}
              </p>
            </div>

            {/* RESPONSABILITÀ */}
            <div className="bg-[var(--lssd-olive)] border border-[var(--lssd-khaki)]/25 rounded-2xl p-7 lg:col-span-1" data-testid="division-responsibilities">
              <div className="h-10 w-10 rounded-full bg-[var(--lssd-khaki)]/15 border border-[var(--lssd-khaki)]/40 flex items-center justify-center mb-4">
                <ClipboardList className="h-4 w-4 text-[var(--lssd-khaki)]" />
              </div>
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] font-bold text-[var(--lssd-khaki)] mb-3">
                Responsabilità
              </div>
              <ul className="space-y-2">
                {division.responsibilities.map((r) => (
                  <li key={r} className="flex items-start gap-2 text-[14px] text-[var(--lssd-cream)]/85 leading-relaxed">
                    <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[var(--lssd-khaki)] shrink-0" />
                    <span>{r}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* STRUTTURA */}
            <div className="bg-[var(--lssd-olive)] border border-[var(--lssd-khaki)]/25 rounded-2xl p-7" data-testid="division-structure">
              <div className="h-10 w-10 rounded-full bg-[var(--lssd-khaki)]/15 border border-[var(--lssd-khaki)]/40 flex items-center justify-center mb-4">
                <Layers className="h-4 w-4 text-[var(--lssd-khaki)]" />
              </div>
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] font-bold text-[var(--lssd-khaki)] mb-2">
                Struttura
              </div>
              <p className="text-[var(--lssd-cream)]/85 text-[15px] leading-relaxed">
                {division.structure}
              </p>
            </div>

          </div>
        </div>
      </section>

      {/* ============ GALLERY OPZIONALE (carousel se >1 foto) ============ */}
      {division.gallery && division.gallery.length > 0 && (
        <section className="py-16 sm:py-20 bg-[var(--lssd-olive-deep)] border-b border-[var(--lssd-khaki)]/15" data-testid="division-gallery">
          <div className="max-w-6xl mx-auto px-6 sm:px-10">
            <div className="flex items-baseline justify-between mb-8 flex-wrap gap-3">
              <div>
                <div className="kicker mb-2">Sul campo</div>
                <h2 className="font-display font-black text-2xl sm:text-3xl text-[var(--lssd-cream)] tracking-tight">
                  {division.code} in operazione
                </h2>
              </div>
              {division.gallery.length > 1 && (
                <span className="font-mono text-[10px] uppercase tracking-[0.22em] font-semibold text-[var(--lssd-khaki)]/70">
                  {division.gallery.length} scatti · auto-scroll
                </span>
              )}
            </div>
            <DivisionGallery
              images={division.gallery}
              testIdPrefix={`division-gallery-${division.code}`}
            />
          </div>
        </section>
      )}

      {/* ============ ALTRE DIVISIONI ============ */}
      <section className="py-16 sm:py-20 bg-[var(--lssd-olive-deep)]">
        <div className="max-w-6xl mx-auto px-6 sm:px-10">
          <div className="flex items-baseline justify-between mb-8 flex-wrap gap-3">
            <div>
              <div className="kicker mb-2">Altre unità</div>
              <h2 className="font-display font-black text-2xl sm:text-3xl text-[var(--lssd-cream)] tracking-tight">
                Scopri le altre divisioni
              </h2>
            </div>
            <Link
              to="/divisioni"
              data-testid="division-back-to-index"
              className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.2em] font-semibold text-[var(--lssd-khaki)] hover:text-[var(--lssd-cream)] transition-colors"
            >
              Tutte le divisioni <ArrowRight className="h-3 w-3" />
            </Link>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            {others.map((d) => {
              const OtherIcon = d.icon;
              return (
                <Link
                  key={d.code}
                  to={`/divisioni/${d.slug}`}
                  data-testid={`other-division-${d.code}`}
                  className="group bg-[var(--lssd-olive)] border border-[var(--lssd-khaki)]/20 rounded-2xl p-5 hover:border-[var(--lssd-khaki)]/60 transition-all"
                >
                  <div className="h-10 w-10 rounded-full bg-[var(--lssd-khaki)]/12 border border-[var(--lssd-khaki)]/30 flex items-center justify-center mb-3 group-hover:bg-[var(--lssd-khaki)]/20 transition-colors">
                    <OtherIcon className="h-4 w-4 text-[var(--lssd-khaki)]" />
                  </div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.22em] font-semibold text-[var(--lssd-khaki)] mb-1.5">
                    {d.code}
                  </div>
                  <h3 className="font-display font-bold text-[15px] text-[var(--lssd-cream)] mb-1 leading-tight">
                    {d.name}
                  </h3>
                  <p className="text-[13px] text-[var(--lssd-cream)]/60 leading-snug line-clamp-2">
                    {d.short}
                  </p>
                </Link>
              );
            })}
          </div>

          <div className="mt-10 text-center">
            <Link
              to="/divisioni"
              data-testid="division-cta-all"
              className="inline-flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4 text-[var(--lssd-khaki)]" />
              <span className="btn-inst-outline">Torna a tutte le divisioni</span>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
